import type { HotelBranding, ThemeMode } from "../bridge/contract";

/**
 * Hotel branding, derived rather than trusted.
 *
 * Same approach as the guest app's `src/utils/contrast.ts`: one accent colour from the hotel's
 * configuration, everything else computed, and the result clamped so text on the accent stays
 * legible. A hotel whose brand colour is a bright yellow would otherwise ship white-on-yellow
 * buttons at a 1.3:1 ratio, and the booking funnel is the last place to lose people to that.
 *
 * Copied, not imported: the booking app and the guest app are deliberately separate builds.
 */

const WCAG_AA_NORMAL = 4.5;
const FALLBACK_ACCENT = "#14b8a6";

export type DerivedPalette = {
  /** Fill colour — button and chip backgrounds. Stays the hotel's brand colour. */
  accent: string;
  hover: string;
  soft: string;
  /** Text placed on top of `accent`. */
  onAccent: string;
  /**
   * The same brand colour, darkened or lightened until it is readable *on the page*.
   *
   * Two tokens rather than one because the two uses have different requirements: a button only
   * has to carry its own label, while accent-coloured text and focus rings sit on the page
   * surface. Clamping one value for both is what turned a perfectly usable teal into a muddy
   * green — the brand has to survive the accessibility pass, not be replaced by it.
   */
  accentText: string;
};

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const shorthand = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const full = hex.replace(shorthand, (_, r, g, b) => `${r}${r}${g}${g}${b}${b}`);
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(full);
  if (!match) return null;
  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16),
  };
}

export function getLuminance(r: number, g: number, b: number): number {
  const channels = [r, g, b].map((value) => {
    const normalized = value / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
}

export function getContrastRatio(l1: number, l2: number): number {
  const brightest = Math.max(l1, l2);
  const darkest = Math.min(l1, l2);
  return (brightest + 0.05) / (darkest + 0.05);
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Derives an accessible palette from a hotel accent.
 *
 * The surface differs between light and dark mode, so the clamp runs against the surface the
 * accent will actually sit on — darkening a colour against white is wrong when the page is dark.
 */
type Rgb = { r: number; g: number; b: number };

/** Steps a colour toward the given direction until it clears the ratio, or gives up. */
function clampToward(start: Rgb, targetLuminance: number, lighten: boolean): Rgb {
  const adjusted = { ...start };
  let ratio = getContrastRatio(getLuminance(adjusted.r, adjusted.g, adjusted.b), targetLuminance);
  let step = 0;
  while (ratio < WCAG_AA_NORMAL && step < 32) {
    if (lighten) {
      adjusted.r = Math.min(255, Math.round(adjusted.r + (255 - adjusted.r) * 0.12) + 1);
      adjusted.g = Math.min(255, Math.round(adjusted.g + (255 - adjusted.g) * 0.12) + 1);
      adjusted.b = Math.min(255, Math.round(adjusted.b + (255 - adjusted.b) * 0.12) + 1);
    } else {
      adjusted.r = Math.max(0, Math.round(adjusted.r * 0.9));
      adjusted.g = Math.max(0, Math.round(adjusted.g * 0.9));
      adjusted.b = Math.max(0, Math.round(adjusted.b * 0.9));
    }
    ratio = getContrastRatio(getLuminance(adjusted.r, adjusted.g, adjusted.b), targetLuminance);
    step += 1;
  }
  return adjusted;
}

/** Best readable label colour for a fill, and the ratio it achieves. */
function bestLabel(fill: Rgb): { color: string; ratio: number } {
  const luminance = getLuminance(fill.r, fill.g, fill.b);
  const onBlack = getContrastRatio(luminance, 0);
  const onWhite = getContrastRatio(luminance, 1);
  return onBlack > onWhite ? { color: "#111111", ratio: onBlack } : { color: "#ffffff", ratio: onWhite };
}

export function derivePalette(accentHex: string | null | undefined, surfaceHex: string): DerivedPalette {
  const fallback: DerivedPalette = {
    accent: FALLBACK_ACCENT,
    hover: "#0f766e",
    soft: "rgba(20, 184, 166, 0.10)",
    onAccent: "#ffffff",
    accentText: "#0f766e",
  };

  if (!accentHex || !/^#([0-9a-f]{3}){1,2}$/i.test(accentHex)) return fallback;

  const rgb = hexToRgb(accentHex);
  const surface = hexToRgb(surfaceHex);
  if (!rgb || !surface) return fallback;

  const surfaceLuminance = getLuminance(surface.r, surface.g, surface.b);
  const surfaceIsDark = surfaceLuminance < 0.5;

  // The fill keeps the hotel's colour. It is only corrected in the rare case where neither
  // black nor white text would be readable on it — a mid-tone that carries no label at all.
  let fill = { ...rgb };
  if (bestLabel(fill).ratio < WCAG_AA_NORMAL) {
    fill = clampToward(fill, surfaceIsDark ? 1 : 0, surfaceIsDark);
  }

  // The text form is corrected against the page, in whichever direction that page needs.
  const textRgb = clampToward({ ...rgb }, surfaceLuminance, surfaceIsDark);

  const hoverFactor = surfaceIsDark ? 1.12 : 0.85;
  const hover = rgbToHex(
    Math.max(0, Math.min(255, Math.round(fill.r * hoverFactor))),
    Math.max(0, Math.min(255, Math.round(fill.g * hoverFactor))),
    Math.max(0, Math.min(255, Math.round(fill.b * hoverFactor))),
  );

  return {
    accent: rgbToHex(fill.r, fill.g, fill.b),
    hover,
    soft: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.10)`,
    onAccent: bestLabel(fill).color,
    accentText: rgbToHex(textRgb.r, textRgb.g, textRgb.b),
  };
}

export function resolveThemeMode(mode: ThemeMode): "light" | "dark" {
  if (mode === "light" || mode === "dark") return mode;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

const LIGHT_SURFACE = "#ffffff";
const DARK_SURFACE = "#14161a";

/**
 * Applies the hotel's look to the document.
 *
 * The hotel's configured mode wins over the visitor's system preference: the widget sits inside
 * someone else's page, and a dark booking panel on a light hotel site reads as broken.
 */
export function applyHotelBranding(branding: HotelBranding, root: HTMLElement): "light" | "dark" {
  const mode = resolveThemeMode(branding.themeMode);
  const surface = mode === "dark" ? DARK_SURFACE : LIGHT_SURFACE;
  const palette = derivePalette(branding.accentColor, surface);

  root.dataset.theme = mode;
  root.style.setProperty("--hotel-accent", palette.accent);
  root.style.setProperty("--hotel-accent-hover", palette.hover);
  root.style.setProperty("--hotel-accent-soft", palette.soft);
  root.style.setProperty("--hotel-accent-on", palette.onAccent);
  root.style.setProperty("--hotel-accent-text", palette.accentText);
  return mode;
}
